<script lang="ts">
    import {
        darkmodePreference,
        easyFontPreference,
        fontSize,
        styleFontSize,
    } from "@store/deviceSettings"
    import Slider from "@components/highlighting/Slider.svelte"
    import Switch from "@components/material/Switch.svelte"
    import { acim } from "@utils/acim"
    import { clickEnter } from "@utils/directives/accessibility"
    import { htmlRenderItem } from "@utils/reading/render"

    let override = $darkmodePreference != "auto"
    let dark = $darkmodePreference == "dark"

    $: if (!override) {
        $darkmodePreference = "auto"
    } else if (dark) {
        $darkmodePreference = "dark"
    } else {
        $darkmodePreference = "light"
    }

    const segment = acim.findRenderable("T-1.8").renderItems[0]
</script>

<section class="appearance-panel">
    <div class="appearance-header">
        <span>Appearance</span>
        <h2>Reading display</h2>
    </div>

    <div class="setting-card">
        <h3>Dark mode</h3>
        <div class="setting-row">
            <Switch
                bind:toggled={override}
                class="setting-switch"
                label="override device preference"
            />
            <span>Override device preference</span>
        </div>
        <div class="setting-row" class:disabled={!override}>
            <Switch disabled={!override} bind:toggled={dark} label="enable dark mode" />
            <span>Enable dark mode</span>
        </div>
    </div>

    <div class="setting-card">
        <h3>Easy font</h3>
        <div class="setting-row">
            <Switch bind:toggled={$easyFontPreference} label="enable easy font" />
            <span>Enable easy font mode</span>
        </div>
    </div>

    <div class="setting-card">
        <h3>Font size</h3>
        <div class="font-size-control">
            <button
                class="letter small"
                type="button"
                aria-label="make font size smaller"
                on:keydown={clickEnter}
                on:click={() => ($fontSize = 1)}
            >
                a
            </button>
            <div class="slider">
                <Slider min={1} max={2} bind:percentage={$fontSize} small={true} />
            </div>
            <button
                class="letter big"
                type="button"
                aria-label="make font size bigger"
                on:keydown={clickEnter}
                on:click={() => ($fontSize = 2)}
            >
                a
            </button>
        </div>
    </div>

    <div class="reader preview-card font-serif" use:styleFontSize>
        {@html htmlRenderItem(segment)}
    </div>
</section>

<style>
    .appearance-panel {
        display: grid;
        gap: 1rem;
    }

    .appearance-header {
        display: grid;
        gap: 0.35rem;
        padding-right: 2.5rem;
    }

    .appearance-header span {
        color: var(--color-purple-700);
        font-size: 0.75rem;
        font-weight: 700;
        letter-spacing: 0.025em;
        line-height: 1;
        text-transform: uppercase;
    }

    .appearance-header h2,
    .setting-card h3 {
        margin: 0;
        color: var(--color-grey-900);
        letter-spacing: 0;
    }

    .appearance-header h2 {
        font-family: Lora, ui-serif, Georgia, Cambria, "Times New Roman", Times, serif;
        font-size: 1.6rem;
        font-weight: 500;
        line-height: 1.25;
    }

    .setting-card {
        display: grid;
        gap: 0.75rem;
        border: 1px solid var(--color-purple-100);
        border-radius: 1rem;
        background: var(--color-grey-0);
        padding: 1rem;
    }

    .setting-card h3 {
        font-size: 1rem;
        font-weight: 750;
        line-height: 1.3;
    }

    .setting-row {
        display: flex;
        align-items: center;
        gap: 0.65rem;
        color: var(--color-grey-600);
        font-size: 0.95rem;
        font-weight: 650;
        line-height: 1.35;
    }

    .setting-row.disabled {
        filter: grayscale(0.75);
        opacity: 0.55;
    }

    .font-size-control {
        display: grid;
        grid-template-columns: auto minmax(0, 1fr) auto;
        align-items: center;
        gap: 0.85rem;
    }

    .letter {
        appearance: none;
        display: grid;
        place-items: center;
        width: 2.4rem;
        height: 2.4rem;
        border: 1px solid var(--color-purple-100);
        border-radius: 0.65rem;
        background: var(--color-grey-0);
        color: var(--color-purple-700);
        cursor: pointer;
        font-family: Lora, ui-serif, Georgia, Cambria, "Times New Roman", Times, serif;
        font-weight: 700;
        line-height: 1;
    }

    .letter.big {
        font-size: 1.35rem;
    }

    .letter:focus-visible {
        outline: 2px solid var(--color-gold-400);
        outline-offset: 2px;
    }

    .preview-card {
        border: 1px solid var(--color-purple-100);
        border-radius: 1rem;
        background: var(--color-purple-50);
        color: var(--color-grey-900);
        padding: 1rem;
    }

    :global(.darkmode) .appearance-header span {
        color: var(--color-purple-200);
    }

    :global(.darkmode) .appearance-header h2,
    :global(.darkmode) .setting-card h3,
    :global(.darkmode) .preview-card {
        color: var(--color-grey-50);
    }

    :global(.darkmode) .setting-card,
    :global(.darkmode) .letter {
        border-color: var(--color-grey-700);
        background: var(--color-grey-900);
    }

    :global(.darkmode) .setting-row {
        color: var(--color-grey-200);
    }

    :global(.darkmode) .letter {
        color: var(--color-purple-100);
    }

    :global(.darkmode) .preview-card {
        border-color: var(--color-grey-700);
        background: var(--color-grey-800);
    }
</style>
